import { useEffect, useMemo, useState } from 'react';

import { apiDelete, apiGet, apiPost, apiPut } from '../lib/api';
import {
  integrationGroups,
  providerGuides,
  providerGroupMap,
  type Provider,
} from '../lib/integration-guides';

interface Integration {
  provider: Provider;
  display_name: string;
  status: string;
  last_error: string | null;
}

interface IntegrationDetail {
  provider: Provider;
  display_name: string;
  status: string;
  last_error: string | null;
  credentials: Array<{
    key_name: string;
    key_label: string;
    value: string;
    is_secret: boolean;
  }>;
}

interface WiseProfileResult {
  id: number;
  type: string;
  name: string;
  suggested: boolean;
}

export function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [detail, setDetail] = useState<IntegrationDetail | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [wiseProfiles, setWiseProfiles] = useState<WiseProfileResult[]>([]);
  const [wiseProfilesBusy, setWiseProfilesBusy] = useState(false);
  const [providerLoading, setProviderLoading] = useState<Provider | null>(null);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [deletingCredentialKey, setDeletingCredentialKey] = useState<string | null>(null);

  const loadList = async () => {
    const list = await apiGet<Integration[]>('/api/integrations');
    setIntegrations(list);
  };

  const loadDetail = async (provider: Provider) => {
    const data = await apiGet<IntegrationDetail>(`/api/integrations/${provider}`);
    setDetail(data);
    setFormValues(Object.fromEntries(data.credentials.map((item) => [item.key_name, ''])));
    setWiseProfiles([]);
  };

  useEffect(() => {
    void (async () => {
      try {
        await loadList();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load integrations');
      }
    })();
  }, []);

  const selected = useMemo(
    () => integrations.find((item) => item.provider === selectedProvider) ?? null,
    [integrations, selectedProvider],
  );

  const groupedIntegrations = useMemo(
    () =>
      integrationGroups
        .map((group) => ({
          group,
          items: integrations.filter((item) => group.providers.includes(item.provider)),
        }))
        .filter((section) => section.items.length > 0),
    [integrations],
  );

  return (
    <div>
      <h1 className="text-xl font-semibold">Integrations</h1>
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
      {message ? <p className="mt-2 text-sm text-green-700">{message}</p> : null}

      <div className="mt-4 space-y-5">
        {groupedIntegrations.map((section) => (
          <section key={section.group.id}>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
              {section.group.title}
            </h2>
            <p className="mt-1 text-xs text-slate-500">{section.group.description}</p>

            <div className="mt-2 grid gap-3 md:grid-cols-2">
              {section.items.map((item) => (
                <button
                  key={item.provider}
                  type="button"
                  className={`rounded-md border px-3 py-3 text-left ${selectedProvider === item.provider ? 'border-brand-600 bg-brand-50' : 'border-slate-300 bg-white'}`}
                  onClick={async () => {
                    if (providerLoading) {
                      return;
                    }
                    setError(null);
                    setSelectedProvider(item.provider);
                    setProviderLoading(item.provider);
                    try {
                      await loadDetail(item.provider);
                    } catch (err) {
                      setError(
                        err instanceof Error ? err.message : 'Failed to load integration detail',
                      );
                    } finally {
                      setProviderLoading(null);
                    }
                  }}
                  disabled={providerLoading !== null}
                >
                  <p className="font-medium">{item.display_name}</p>
                  <p className="text-sm text-slate-600">Status: {item.status}</p>
                  {item.last_error ? (
                    <p className="mt-1 text-xs text-red-600">{item.last_error}</p>
                  ) : null}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      {detail && selected ? (
        <div className="mt-6 rounded-md border border-slate-200 p-4">
          <h2 className="text-lg font-semibold">{detail.display_name}</h2>
          <p className="mt-1 text-xs text-slate-500">
            Group: {providerGroupMap.get(detail.provider)?.title ?? 'General'}
          </p>

          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
            <h3 className="text-sm font-semibold">Configuration Method And Guidance</h3>
            <p className="mt-1 text-sm text-slate-700">{providerGuides[detail.provider].purpose}</p>
            <p className="mt-1 text-sm text-slate-700">
              <span className="font-medium">Method:</span>{' '}
              {providerGuides[detail.provider].setupMethod}
            </p>

            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-700">
              {providerGuides[detail.provider].steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>

            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
              {providerGuides[detail.provider].guidance.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>

            <p className="mt-2 text-xs text-slate-500">
              Required fields:{' '}
              {detail.credentials.map((credential) => credential.key_label).join(', ')}
            </p>
          </div>

          <div className="mt-3 space-y-3">
            {detail.credentials.map((credential) => (
              <div key={credential.key_name}>
                <label className="mb-1 block text-sm font-medium">{credential.key_label}</label>
                <div className="flex gap-2">
                  <input
                    type={credential.is_secret ? 'password' : 'text'}
                    value={formValues[credential.key_name] ?? ''}
                    onChange={(event) =>
                      setFormValues((prev) => ({
                        ...prev,
                        [credential.key_name]: event.target.value,
                      }))
                    }
                    placeholder={`Current: ${credential.value}`}
                    className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                    onClick={async () => {
                      setError(null);
                      setMessage(null);
                      setDeletingCredentialKey(credential.key_name);
                      try {
                        await apiDelete<{ status: string }>(
                          `/api/integrations/${detail.provider}/credentials/${credential.key_name}`,
                        );
                        await loadDetail(detail.provider);
                        await loadList();
                        setMessage('Credential deleted');
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Delete failed');
                      } finally {
                        setDeletingCredentialKey(null);
                      }
                    }}
                    disabled={
                      deletingCredentialKey !== null || savingCredentials || testingConnection
                    }
                  >
                    {deletingCredentialKey === credential.key_name ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {detail.provider === 'wise' ? (
            <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-emerald-900">Find Wise profile IDs</h3>
                  <p className="mt-1 text-sm text-emerald-800">
                    Wise usually calls this value `profileId`. We can query `/v2/profiles` with your
                    API token and show the available IDs here.
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm text-emerald-900"
                  disabled={wiseProfilesBusy}
                  onClick={async () => {
                    setError(null);
                    setMessage(null);
                    setWiseProfilesBusy(true);
                    try {
                      const response = await apiPost<{ profiles: WiseProfileResult[] }>(
                        '/api/integrations/wise/discover-profiles',
                        formValues.api_token ? { api_token: formValues.api_token } : {},
                        { silentSuccess: true },
                      );
                      setWiseProfiles(response.profiles);
                    } catch (err) {
                      setError(
                        err instanceof Error ? err.message : 'Failed to discover Wise profiles',
                      );
                    } finally {
                      setWiseProfilesBusy(false);
                    }
                  }}
                >
                  {wiseProfilesBusy ? 'Finding...' : 'Find profiles'}
                </button>
              </div>

              {wiseProfiles.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {wiseProfiles.map((profile) => (
                    <button
                      key={profile.id}
                      type="button"
                      className="block w-full rounded-md border border-emerald-200 bg-white px-3 py-3 text-left hover:border-emerald-300"
                      onClick={() => {
                        setFormValues((prev) => ({ ...prev, profile_id: String(profile.id) }));
                        setMessage(`Loaded profile_id ${profile.id} into the form`);
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-900">{profile.name}</p>
                          <p className="text-sm text-slate-600">
                            ID {profile.id} · type {profile.type}
                          </p>
                        </div>
                        {profile.suggested ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800">
                            Suggested
                          </span>
                        ) : null}
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white"
              onClick={async () => {
                setError(null);
                setMessage(null);
                setSavingCredentials(true);
                try {
                  await apiPut<{ status: string }>(`/api/integrations/${detail.provider}`, {
                    credentials: formValues,
                  });
                  await loadDetail(detail.provider);
                  await loadList();
                  setMessage('Credentials updated');
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Update failed');
                } finally {
                  setSavingCredentials(false);
                }
              }}
              disabled={savingCredentials || testingConnection || deletingCredentialKey !== null}
            >
              {savingCredentials ? 'Saving...' : 'Save credentials'}
            </button>

            <button
              type="button"
              className="rounded-md border border-slate-300 px-4 py-2 text-sm"
              onClick={async () => {
                setError(null);
                setMessage(null);
                setTestingConnection(true);
                try {
                  await apiPost<{ status: string }>(
                    `/api/integrations/${detail.provider}/test`,
                    {},
                  );
                  await loadDetail(detail.provider);
                  await loadList();
                  setMessage('Connection test passed');
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Connection test failed');
                } finally {
                  setTestingConnection(false);
                }
              }}
              disabled={testingConnection || savingCredentials || deletingCredentialKey !== null}
            >
              {testingConnection ? 'Testing...' : 'Test connection'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
